import React from 'react';
import { get, filter, map, find, cloneDeep, isNil, merge, isEmpty } from 'lodash';

// React view component to render
import { memberInfoCommunicationSettingsPhone as MemberInfoCommunicationSettingsPhoneView } from 'ui/views/widgets/memberInfoCommunicationSettingsPhone';
import { MemberInfoCommunicationSettingsPhoneService } from 'services/memberInfoCommunicationSettingsPhone';
import * as confirmOverlay from 'utils/confirmOverlay';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { getLabel } from 'utils/getLabel';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, GLOBAL_LABELS, SIZES } from 'constants/common';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberInfoCommunicationSettingsPhone } from 'ui/containers/MemberInfoCommunicationSettingsPhone';
 *
 * // render
 * <MemberInfoCommunicationSettingsPhone propName='propValue'/>
 */
export class MemberInfoCommunicationSettingsPhone extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind functions
        this.deleteActionHandler = this.deleteActionHandler.bind( this );
        this.defaultActionHandler = this.defaultActionHandler.bind( this );
        this.addEditFormHandler = this.addEditFormHandler.bind( this );
        this.handleSetCommunicationPreference = this.handleSetCommunicationPreference.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            showCustomErrorView: false
        };

        // Member Info Phone Settings Service
        this.memberInfoCommunicationSettingsPhoneService = new MemberInfoCommunicationSettingsPhoneService();

        // Member Info Phone Settings data fetch
        this.fetchComponentData();
    }

    /**
     * @desc Handles fetching of component data
     */
    async fetchComponentData() {
        log.debug( 'MemberInfoCommunicationSettingsPhone.fetchComponentData()' );

        // cancel ongoing request
        this.memberInfoCommunicationSettingsPhoneService.cancel();

        try {
            const { status, data }  = await this.memberInfoCommunicationSettingsPhoneService.fetchData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                if( ! isEmpty( get( data, 'description', {} ) ) ) {
                    this.setState( {
                        isFetching: false,
                        showCustomErrorView: true,
                        componentData: data
                    } );
                } else {
                    this.setState( {
                        isFetching: false,
                        networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                    } );
                }
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                } );
            }
        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    /**
     * @desc Handles deletion of record
     */
    async deleteActionHandler( { item } ) {
        log.debug( 'MemberInfoCommunicationSettingsPhone.deleteActionHandler()' );
        
        // get delete action from component data
        const deleteAction = find( this.state.componentData.modifyActions, { name: 'delete' } );

        // do not delete if contact is default
        if( ! item.flags.isDefault ) {

            const shouldProceed = await confirmOverlay.open( {
                message: getLabel( deleteAction.confirmation ),
                iconName: 'close'
            } );

            if ( shouldProceed ) {

                // cancel ongoing request
                this.memberInfoCommunicationSettingsPhoneService.cancel();

                // show page level preloader
                pageLevelPreloader.show();

                // prepare request payload
                const reqParams = cloneDeep( this.props.requestPayload );
                reqParams.context.operation = CRUD_OPERATION.DELETE;

                // get status and service data
                const { status, data } = await this.memberInfoCommunicationSettingsPhoneService.deleteItem( {
                    url: deleteAction.url,
                    data: merge(
                        { data: {
                        id: item.id
                    } }, reqParams ),
                } );

                // hide page level preloader
                pageLevelPreloader.hide();

                // check if response status was success
                if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                    this.setState( {
                        networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                        componentData: {
                            ...this.state.componentData,
                            contacts: filter(
                                this.state.componentData.contacts,
                                ( contact ) => contact.id !== item.id
                            ),
                        },
                    } );
                } else {
                    
                    // show error dialog
                    if( ! isEmpty( get( data, 'description' ) ) ) {
                        errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                    } else {
                        errorOverlay.open();
                    }
                }
            }
        } else {
            errorOverlay.open( {
                message: getLabel( deleteAction.prohibit ),
                size: 'small'
            } );
        }
    }

    /**
     * @desc Handles add/edit form
     */
    async addEditFormHandler ( { id, type, contact, isDefault = false } ) {
        log.debug( 'MemberInfoCommunicationSettingsPhone.addEditFormHandler()' );

        // get from add action from component data
        const formAction = this.state.componentData.addAction;

        // cancel ongoing request
        this.memberInfoCommunicationSettingsPhoneService.cancel();

        // prepare request payload
        const reqParams = cloneDeep( this.props.requestPayload );
        reqParams.context.operation = isNil( id ) ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE;

        // show page level preloader
        pageLevelPreloader.show();
        
        // get status and service data
        const { _status, data } = await this.memberInfoCommunicationSettingsPhoneService.addUpdatePhoneNumber( {
            url: formAction.config.submitUrl,
            data: merge(
                {
                    data: {
                        id: id,
                        type: type,
                        contact: contact,
                        isDefault: isNil( id ) ? false : isDefault
                    }
                }, reqParams ),
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {
            if( this.state.componentData.updateMemberPreferencesRequest !=null ){
            const updateMemberPreferencesRequest = this.state.componentData.updateMemberPreferencesRequest;
            const phoneObj = updateMemberPreferencesRequest.member.channelPreferences.communicationChannels.find(obj => obj.channel.channelName == "CALL");
            
                phoneObj.channelAddress = contact;
                const { status, data } = await this.memberInfoCommunicationSettingsPhoneService.setCommunicationPreference( {
                    url:  this.state.componentData.communicationPreferenceAction.url, //getting the url of action
                    data: merge(               
                    {data:{memberChannelPreferences: updateMemberPreferencesRequest}}, 
                    this.props.requestPayload ),

                } );
            
        }
            
            // fetch items from the backend again
            this.setState( {
                isFetching: true
            }, () => {
                this.fetchComponentData();
            } );
        } else {
                   
            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc Handles set default action
     */
    async defaultActionHandler( { item } ) {
        log.debug( 'MemberInfoCommunicationSettingsPhone.defaultActionHandler()' );

        // don't do anything if contact is already a default contact
        if( item.flags.isDefault ) {
            return;
        }

        // get set default action from component data
        const defaultAction = find( this.state.componentData.modifyActions, { name: 'setDefault' } );

        // cancel ongoing request
        this.memberInfoCommunicationSettingsPhoneService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberInfoCommunicationSettingsPhoneService.defaultItem( {
            url: defaultAction.url,
            data: merge( {
                data: {
                id: item.id
            } }, this.props.requestPayload ),

        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            const updateMemberPreferencesRequest = this.state.componentData.updateMemberPreferencesRequest;
            const phoneObj = updateMemberPreferencesRequest.member.channelPreferences.communicationChannels.find(obj => obj.channel.channelName == "CALL");
            if (phoneObj.optedIn) {
                
                phoneObj.channelAddress = item.contact;
                const { status, data } = await this.memberInfoCommunicationSettingsPhoneService.setCommunicationPreference( {
                    url:  this.state.componentData.communicationPreferenceAction.url, //getting the url of action
                    data: merge(               
                    {data:{memberChannelPreferences: updateMemberPreferencesRequest}}, 
                    this.props.requestPayload ),

                } );
            }
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: {
                    ...this.state.componentData,
                    contacts: map(
                        this.state.componentData.contacts,
                        ( contact ) => {
                            contact.id === item.id ? contact.flags.isDefault= true : contact.flags.isDefault = false;
                            return contact;
                        }
                    ),
                },
            }, () => {
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );
        } else {

            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /*
     * @desc Handles set communication Preference action
     */
    async handleSetCommunicationPreference(payload) {
        log.debug( 'MemberInfoCommunicationSettingsPhone.handleSetCommunicationPreference()' );

        const obj = payload;
        const {communicationPreferenceFlag} = payload;
        const phoneObj = obj.updateMemberPreferencesRequest.member.channelPreferences.communicationChannels.find(obj => obj.channel.channelName == "CALL");
        phoneObj.optedIn = payload.communicationPreferenceFlag;
        phoneObj.channelAddress = this.state?.componentData?.contacts?.find( obj => obj.flags.isDefault)?.contact;
        
        // cancel ongoing request
        this.memberInfoCommunicationSettingsPhoneService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data

        const { status, data } = await this.memberInfoCommunicationSettingsPhoneService.setCommunicationPreference( {
            url: this.state.componentData.communicationPreferenceAction.url, //getting the url of action
            data: merge(               
              {data:{memberChannelPreferences: obj.updateMemberPreferencesRequest}}, 
              this.props.requestPayload ),

        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: {
                    ...this.state.componentData,
                    ...communicationPreferenceFlag,
                },
            }, () => {
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );
        } else {

            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }

    }

    /**
     * @desc When component unmounts, cleanup resources.
     */
    componentWillUnmount() {

        // cancel ongoing requests
        this.memberInfoCommunicationSettingsPhoneService.cancel();
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberInfoCommunicationSettingsPhoneView
                isFetching= { this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                requestPayload={ this.props.requestPayload }
                { ...get( this.state, 'componentData', {} ) }
                onDeleteClick = { this.deleteActionHandler }
                onDefaultClick = { this.defaultActionHandler }
                onAddEditClick = { this.addEditFormHandler }
                showCustomErrorView={ this.state.showCustomErrorView }
                onSetCommunicationPreferenceClick = { this.handleSetCommunicationPreference }
            />
        );
    }
}

// set display name
MemberInfoCommunicationSettingsPhone.displayName = 'MemberInfoCommunicationSettingsPhone';

// set default props
MemberInfoCommunicationSettingsPhone.defaultProps = {};

// prop types
MemberInfoCommunicationSettingsPhone.propTypes = {};
